import React, { useState, useEffect, useMemo } from "react";
import {
  formatDate,
  getDatesOfSelectedDays,
  getFullName,
  getNameChars,
} from "../../../utils/CommonFunctions";
import { jobTitleOptions } from "../../../constants/constants";
import { currentCareSiteId } from "../../../redux-toolkit/reducer/switchSites";
import { httpAdmin, limitToTwoDecimal } from "../../../Apis/commonApis";
import { ShowSwal } from "../../../utils/alertSwal";

// Style object containing all styles
const styles = {
  // Container styles
  mainContainer: {
    fontFamily: "sans-serif",
    backgroundColor: "#f3f4f6",
    padding: "1.25rem",
    minHeight: "100vh",
  },
  contentWrapper: {
    maxWidth: "56rem",
    margin: "0 auto",
    backgroundColor: "white",
    borderRadius: "0.75rem",
    padding: "1.25rem",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
  },
  centerContent: {
    marginBottom: "2rem",
    textAlign: "center",
  },

  // Typography styles
  mainTitle: {
    color: "#1f2937",
    fontSize: "1.875rem",
    marginBottom: "0.75rem",
    fontWeight: "600",
  },
  subtitle: {
    color: "#6b7280",
    fontSize: "1rem",
    marginBottom: "1.5rem",
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "1rem",
  },

  // Button styles
  primaryButton: {
    backgroundColor: "#9e61e7",
    color: "white",
    border: "none",
    padding: "1rem 2rem",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    fontWeight: "500",
    cursor: "pointer",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s",
  },
  primaryButtonHover: {
    backgroundColor: "#9e61e7",
    transform: "translateY(-2px)",
  },
  secondaryButton: {
    padding: "0.5rem 1.25rem",
    border: "none",
    borderRadius: "0.375rem",
    cursor: "pointer",
    fontSize: "0.875rem",
    fontWeight: "500",
    backgroundColor: "#4b5563",
    color: "white",
  },
  secondaryButtonHover: {
    backgroundColor: "#374151",
  },
  dangerButton: {
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    padding: "0.25rem 0.75rem",
    borderRadius: "0.25rem",
    cursor: "pointer",
    fontSize: "0.75rem",
  },
  dangerButtonHover: {
    backgroundColor: "#b91c1c",
  },
  closeButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "white",
    fontSize: "1.5rem",
    cursor: "pointer",
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonHover: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },

  // Modal styles
  modalOverlay: {
    position: "fixed",
    inset: "0",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "50",
    padding: "2rem 0",
  },
  modalContent: {
    backgroundColor: "white",
    borderRadius: "0.75rem",
    width: "91.666667%",
    maxWidth: "48rem",
    maxHeight: "90vh",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    margin: "auto",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    backgroundColor: "#9e61e7",
    color: "white",
    padding: "1.25rem",
    borderTopLeftRadius: "0.75rem",
    borderTopRightRadius: "0.75rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: {
    fontSize: "1.125rem",
    fontWeight: "600",
    margin: "0",
  },
  modalBody: {
    padding: "1.25rem",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.75rem",
    padding: "1.25rem",
    borderTop: "1px solid #d1d5db",
    backgroundColor: "#f9fafb",
    marginTop: "auto",
  },

  // Card styles
  shiftSummaryCard: {
    backgroundColor: "#f9fafb",
    borderRadius: "0.5rem",
    padding: "1rem",
    marginBottom: "1.25rem",
    borderLeft: "4px solid #9e61e7",
  },
  currentAssignmentCard: {
    backgroundColor: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: "0.375rem",
    padding: "1rem",
    marginBottom: "1.25rem",
  },
  assignmentDisplay: {
    padding: "0.5rem 0.75rem",
    backgroundColor: "white",
    borderRadius: "0.25rem",
    border: "1px solid #bbf7d0",
    fontSize: "0.875rem",
  },

  // Form styles
  textarea: {
    marginTop: "0.5rem",
    width: "100%",
    minHeight: "5rem",
    padding: "0.75rem",
    backgroundColor: "white",
    border: "1px solid #d1d5db",
    borderRadius: "0.25rem",
    fontSize: "0.875rem",
    lineHeight: "1.625",
    resize: "vertical",
  },
  select: {
    padding: "0.5rem 0.75rem",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    fontSize: "0.875rem",
    backgroundColor: "white",
    minWidth: "9rem",
  },
  checkbox: {
    width: "1rem",
    height: "1rem",
    cursor: "pointer",
  },
  label: {
    fontSize: "0.875rem",
    fontWeight: "500",
    color: "#4b5563",
  },

  // Toggle switch styles
  toggleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  toggleLabel: {
    position: "relative",
    display: "inline-block",
    width: "3rem",
    height: "1.5rem",
  },
  toggleInput: {
    opacity: "0",
    width: "0",
    height: "0",
  },
  toggleSlider: (checked) => ({
    position: "absolute",
    cursor: "pointer",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    transition: "all 0.3s",
    borderRadius: "9999px",
    backgroundColor: checked ? "#9e61e7" : "#9ca3af",
  }),
  toggleButton: (checked) => ({
    position: "absolute",
    height: "1rem",
    width: "1rem",
    left: "0.25rem",
    bottom: "0.25rem",
    backgroundColor: "white",
    transition: "all 0.3s",
    borderRadius: "50%",
    transform: checked ? "translateX(1.5rem)" : "translateX(0)",
  }),

  // Staff card styles
  staffGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "0.75rem",
    maxHeight: "18rem",
    overflowY: "auto",
    border: "1px solid #d1d5db",
    borderRadius: "0.375rem",
    padding: "1rem",
    backgroundColor: "#f9fafb",
  },
  staffCard: (isSelected) => ({
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.75rem",
    backgroundColor: isSelected ? "#eff6ff" : "white",
    borderRadius: "0.375rem",
    border: isSelected ? "1px solid #9e61e7" : "1px solid #d1d5db",
  }),
  staffName: {
    fontWeight: "500",
    color: "#1f2937",
    fontSize: "0.875rem",
  },
  staffInfo: {
    fontSize: "0.75rem",
    color: "#6b7280",
  },

  // Accepted staff styles
  acceptedStaffCard: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.75rem",
    backgroundColor: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: "0.375rem",
  },
  avatar: {
    width: "2.5rem",
    height: "2.5rem",
    borderRadius: "50%",
    backgroundColor: "#16a34a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontWeight: "bold",
    fontSize: "0.875rem",
  },
  acceptedBadge: {
    backgroundColor: "#16a34a",
    color: "white",
    padding: "0.25rem 0.5rem",
    borderRadius: "0.25rem",
    fontSize: "0.75rem",
    fontWeight: "500",
    textTransform: "uppercase",
  },
  emptyState: {
    textAlign: "center",
    padding: "2rem",
    color: "#6b7280",
    fontStyle: "italic",
    border: "2px dashed #d1d5db",
    borderRadius: "0.375rem",
    backgroundColor: "#f9fafb",
  },

  // Layout styles
  flexRow: {
    display: "flex",
    gap: "1.25rem",
    marginBottom: "1.25rem",
  },
  flexColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
  },
  gridTwoCols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "0.75rem",
    fontSize: "0.875rem",
  },
  section: {
    marginBottom: "1.5rem",
  },
};

const AgencyShiftUpdate = ({ getShiftsandtaskNew, editTaskData, data }) => {
  const [adminAcceptedStaff, setAdminAcceptedStaff] = useState([]);

  // State
  const [assignStaffToggle, setAssignStaffToggle] = useState(false);
  const [openShiftToggle, setOpenShiftToggle] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [shiftInstructions, setShiftInstructions] = useState(
    editTaskData?.ShiftData?.instruction || ""
  );
  const [hoveredButton, setHoveredButton] = useState(null);
  const [staffCriteria, setStaffCriteria] = useState("weeklyHoursAssigned");
  const [jobTitleFilter, setJobTitleFilter] = useState("");
  const [availableStaffData, setAvailableStaffData] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);

  // Disable body scroll when modal is open
  useEffect(() => {
    // Save the current overflow style
    const originalOverflow = document.body.style.overflow;

    // Disable scrolling on mount
    document.body.style.overflow = "hidden";

    // Re-enable scrolling on unmount
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Set toggle states based on recurringShiftType when component mounts or editTaskData changes
  useEffect(() => {
    const shiftType = editTaskData?.ShiftData?.recurringShiftType;

    if (shiftType === "assigned") {
      setAssignStaffToggle(true);
      setOpenShiftToggle(false);
    } else if (shiftType === "open") {
      setAssignStaffToggle(false);
      setOpenShiftToggle(true);
    } else {
      // If neither 'assigned' nor 'open', disable both toggles
      setAssignStaffToggle(false);
      setOpenShiftToggle(false);
    }
  }, [editTaskData]);

  // Fetch available staff when assignStaffToggle is enabled
  useEffect(() => {
    if (editTaskData) {
      const {
        Date: shiftDate,
        startingTime,
        endingTime,
      } = editTaskData.ShiftData || {};
      const clientId = editTaskData.client?._id;

      if (shiftDate && startingTime && endingTime && clientId) {
        getAvailableCareTeam(
          shiftDate,
          shiftDate, // Using same date for start and end as it appears to be a single shift
          startingTime,
          endingTime,
          clientId
        );
      }
    }
  }, [editTaskData, staffCriteria, jobTitleFilter]);

  const closePopup = () => {
    getShiftsandtaskNew("closePopup");
  };

  const toggleAssignStaff = () => {
    const newValue = !assignStaffToggle;
    setAssignStaffToggle(newValue);

    if (newValue) {
      setOpenShiftToggle(false);

      // Preserve already assigned staff from ShiftMappings
      const alreadyAssignedStaff = [];
      if (editTaskData?.ShiftData?.shiftMappings?.length > 0) {
        editTaskData.ShiftData.shiftMappings.forEach((mapping) => {
          if (mapping.careteam) {
            alreadyAssignedStaff.push({
              id: mapping.careteam._id,
              name: getFullName(mapping.careteam),
            });
          }
        });
      }

      // Add admin accepted staff to selection
      const newSelectedStaff = [...alreadyAssignedStaff];
      adminAcceptedStaff.forEach((staff) => {
        if (!newSelectedStaff.find((s) => s.id === staff.id)) {
          newSelectedStaff.push({
            id: staff.id,
            name: staff.name,
          });
        }
      });
      setSelectedStaff(newSelectedStaff);
    } else {
      setAvailableStaffData((prev) =>
        prev.map((s) => ({ ...s, selected: false }))
      );
      setSelectedStaff([]);
    }
  };

  const toggleOpenShift = () => {
    const newValue = !openShiftToggle;
    setOpenShiftToggle(newValue);

    if (newValue) {
      setAssignStaffToggle(false);
      setSelectedStaff([]);
    } else {
      setAssignStaffToggle(true);
    }
  };

  const toggleStaffSelection = (staffId) => {
    setAvailableStaffData((prev) =>
      prev.map((s) => (s.id === staffId ? { ...s, selected: !s.selected } : s))
    );
  };

  const unassignStaff = (staffId) => {
    setAvailableStaffData((prev) =>
      prev.map((s) => (s.id === staffId ? { ...s, selected: false } : s))
    );
  };

  const getCurrentAssignmentText = () => {
    const staffNames = availableStaffData
      .filter((s) => s.selected)
      .map((s) => s.name);
    if (openShiftToggle) {
      const openShiftStaff = availableStaffData
        .filter((s) => s.selected && s.shiftApprovalstatus)
        .map((s) => s.name);
      if (openShiftStaff?.length > 0) {
        return `Open Shift - ${openShiftStaff.join(", ")}`;
      } else {
        return "Open Shift";
      }
    } else if (staffNames?.length > 0) {
      return staffNames.join(", ");
    } else {
      return "Unassigned";
    }
  };

  const saveAssignment = async () => {
    try {
      const finalData = [];
      const shiftData = editTaskData?.ShiftData;
      const unselectedStaffIds = availableStaffData
        .filter((s) => s.selected === false && s.isAlreadyAssigned)
        .map((s) => s.id);
      const selectedStaffIds = availableStaffData
        .filter((s) => s.selected)
        .map((s) => s.id);

      if (openShiftToggle) {
        // For open shifts
        finalData.push({
          careHomeId: currentCareSiteId,
          clientId: editTaskData?.client?._id,
          shiftId: shiftData?._id,
          Date: shiftData?.Date,
          startingTime: shiftData?.startingTime,
          endingTime: shiftData?.endingTime,
          recurringTask: false,
          recurringTasktype: shiftData?.recurringTasktype || "",
          instruction: shiftInstructions,
          recurringShiftType: "open",
          templateType: "",
          priorityBasedOn: shiftData?.priorityBasedOn || "weeklyHoursAssigned",
          deleteCareteams: unselectedStaffIds,
        });
      } else if (assignStaffToggle && selectedStaffIds.length > 0) {
        // For assigned shifts with selected staff
        finalData.push({
          careHomeId: currentCareSiteId,
          shiftId: shiftData?._id,
          clientId: editTaskData?.client?._id,
          careTeamId: selectedStaffIds,
          Date: shiftData?.Date,
          startingTime: shiftData?.startingTime,
          endingTime: shiftData?.endingTime,
          recurringTask: false,
          recurringTasktype: shiftData?.recurringTasktype || "",
          instruction: shiftInstructions,
          templateType: "",
          priorityBasedOn: shiftData?.priorityBasedOn || "weeklyHoursAssigned",
          recurringShiftType: "assigned",
          deleteCareteams: unselectedStaffIds,
        });
      } else {
        // For unassigned shifts
        finalData.push({
          careHomeId: currentCareSiteId,
          clientId: editTaskData?.client?._id,
          shiftId: shiftData?._id,
          Date: shiftData?.Date,
          startingTime: shiftData?.startingTime,
          endingTime: shiftData?.endingTime,
          recurringTask: false,
          recurringTasktype: shiftData?.recurringTasktype || "",
          instruction: shiftInstructions,
          recurringShiftType: "unassigned",
          templateType: "",
          priorityBasedOn: shiftData?.priorityBasedOn || "weeklyHoursAssigned",
          deleteCareteams: unselectedStaffIds,
        });
      }

      const apiEnd = "createShift?type=edit";
      const res = await httpAdmin(apiEnd, "post", finalData, null);
      ShowSwal(res.status, res.message);
      if (res.status) {
        getShiftsandtaskNew("closePopup");
      }
    } catch (error) {
      console.error("Error saving assignment:", error);
      alert("An error occurred while saving the assignment");
    }
  };

  const getCriteriaInfo = (staff) => {
    // Don't show hours for already assigned staff
    if (staff.isAlreadyAssigned) {
      return "";
    }

    switch (staffCriteria) {
      case "weeklyHoursAssigned":
        return ` • Hours: ${staff.weeklyHoursAssigned}`;
      case "distance":
        return ` • ${staff.distance} miles`;
      case "performance":
        return ` • Performance: ${staff.performance}%`;
      default:
        return ` • Hours: ${staff.weeklyHoursAssigned}`;
    }
  };

  const sortArrayByKey = (arr, key) => {
    return [...arr].sort((a, b) => {
      const valA = Number(a[key]);
      const valB = Number(b[key]);
      if (key === "performance") {
        return valB - valA;
      }
      return valA - valB;
    });
  };

  const getAvailableCareTeam = async (
    startDateValue,
    endDateValue,
    startTimeValue,
    endTimeValue,
    client
  ) => {
    const data = {
      careSiteId: currentCareSiteId,
      priorityBasedOn: staffCriteria,
      // shiftName: filters?.shiftName,
      jobTitle: jobTitleFilter,
      clientId: client,
      shiftDates: [
        {
          date: startDateValue,
          startTime: startTimeValue,
          endTime: endTimeValue,
        },
      ],
      startDate: startDateValue,
      endDate: endDateValue,
    };
    const res = await httpAdmin("availableCareMember", "post", data, null);
    if (res) {
      let teamdata = [];
      res.data?.forEach((item) => {
        teamdata.push({
          ...item,
          id: item._id,
          name: getFullName(item),
          avatar: getNameChars(item),
          image: item.image,
          role: item.jobTitle ?? "",
          jobTitle: item.jobTitle ?? "",
          clientGroup: "all",
          avatar: getNameChars(item),
          hours: item.totalHours ? limitToTwoDecimal(+item.totalHours) : "0",
          weeklyHoursAssigned: item.totalHours
            ? limitToTwoDecimal(+item.totalHours)
            : 0,
          performance: item.averagePerformance
            ? limitToTwoDecimal(+item.averagePerformance)
            : 0,
          distance: item.distance ? limitToTwoDecimal(+item.distance) : 0,
          selected: false,
          isAlreadyAssigned: false,
        });
      });

      // Populate adminAcceptedStaff from shiftMappings for open shifts
      const acceptedStaffFromMappings = editTaskData?.ShiftData?.shiftMappings
        .filter(
          (mapping) => mapping.careteam && mapping.shiftApprovalstatus !== false
        )
        ?.map((mapping) => ({
          ...mapping,
          id: mapping?.careteam?._id,
          name: getFullName(mapping.careteam),
          avatar: getNameChars(mapping.careteam),
          title: mapping.careteam?.jobTitle || "",
          role: mapping.careteam?.jobTitle ?? "",
          jobTitle: mapping.careteam?.jobTitle ?? "",
          acceptedTime: formatDate(mapping.shiftApprovalTimestamp),
          distance: -1,
          performance: 1000,
          shiftHrs: -1,
          image: mapping.careteam.image || null,
          selected: true,
          isAlreadyAssigned: true,
        }));
      const sortTeamData = sortArrayByKey(
        [...acceptedStaffFromMappings, ...teamdata],
        staffCriteria
      );
      setAvailableStaffData(sortTeamData);
    } else {
      setAvailableStaffData([]);
    }
  };

  const getAvailableCareTeamOld = async (
    startDateValue,
    endDateValue,
    startTimeValue,
    endTimeValue,
    client
  ) => {
    setIsLoadingStaff(true);
    const data = {
      careSiteId: currentCareSiteId,
      priorityBasedOn: staffCriteria,
      // shiftName: filters?.shiftName,
      jobTitle: jobTitleFilter,
      clientId: client,
      shiftDates: [
        {
          date: startDateValue,
          startTime: startTimeValue,
          endTime: endTimeValue,
        },
      ],
      startDate: startDateValue,
      endDate: endDateValue,
    };
    const res = await httpAdmin("availableCareMember", "post", data, null);
    if (res.status) {
      let teamdata = [];

      // First, process already assigned users from ShiftMappings
      const assignedStaffIds = new Set();
      const assignedStaffData = [];

      // Process available staff from API, excluding already assigned ones
      res.data?.forEach((item) => {
        if (!assignedStaffIds.has(item._id)) {
          teamdata.push({
            id: item._id,
            name: getFullName(item),
            image: item.image,
            role: item.jobTitle ?? "",
            jobTitle: item.jobTitle ?? "",
            clientGroup: "all",
            avatar: getNameChars(item),
            hours: item.totalHours ? limitToTwoDecimal(+item.totalHours) : "0",
            weeklyHoursAssigned: item.totalHours
              ? limitToTwoDecimal(+item.totalHours)
              : 0,
            performance: item.averagePerformance
              ? limitToTwoDecimal(+item.averagePerformance)
              : 0,
            distance: item.distance ? limitToTwoDecimal(+item.distance) : 0,
            selected: false,
            isAlreadyAssigned: false,
          });
        }
      });

      // Sort the available staff
      const sortedTeamData = sortArrayByKey(teamdata, staffCriteria);

      // Combine assigned staff (at top) with available staff
      const combinedData = [...assignedStaffData, ...sortedTeamData];
      setAvailableStaffData(combinedData);

      // Set selected staff to include already assigned users
      const preSelectedStaff = assignedStaffData.map((staff) => ({
        id: staff.id,
        name: staff.name,
      }));
      setSelectedStaff(preSelectedStaff);
    }
    // Even if no available staff, still show already assigned members
    const assignedStaffData = [];
    if (editTaskData?.ShiftData?.shiftMappings?.length > 0) {
      editTaskData.ShiftData.shiftMappings.forEach((mapping) => {
        if (mapping.careteam) {
          const staffMember = {
            id: mapping.careteam._id,
            name: getFullName(mapping?.careteam),
            image: mapping.careteam?.image || null,
            role: mapping.careteam?.jobTitle ?? "",
            jobTitle: mapping.careteam?.jobTitle ?? "",
            clientGroup: "all",
            avatar: getNameChars(mapping.careteam),
            weeklyHoursAssigned: mapping.careteam?.totalHours
              ? limitToTwoDecimal(+mapping.careteam?.totalHours)
              : 0,
            performance: mapping.careteam?.averagePerformance
              ? limitToTwoDecimal(+mapping.careteam?.averagePerformance)
              : 0,
            distance: mapping.careteam?.distance
              ? limitToTwoDecimal(+mapping.careteam?.distance)
              : 0,
            selected: false,
            isAlreadyAssigned: true,
          };
          assignedStaffData.push(staffMember);
        }
      });
    }
    setAvailableStaffData(assignedStaffData);

    // Set selected staff to include already assigned users
    const preSelectedStaff = assignedStaffData.map((staff) => ({
      id: staff.id,
      name: staff.name,
    }));
    setSelectedStaff(preSelectedStaff);
    setIsLoadingStaff(false);
  };

  const selectedAdminAcceptedStaff = useMemo(() => {
    return availableStaffData.filter(
      (s) => s.selected && s.shiftApprovalstatus
    );
  }, [availableStaffData]);

  return (
    <div style={styles.mainContainer}>
      {/* Popup */}
      <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>Edit Staff Assignment</h2>
            <button
              onClick={closePopup}
              style={{
                ...styles.closeButton,
                ...(hoveredButton === "close" ? styles.closeButtonHover : {}),
              }}
              onMouseEnter={() => setHoveredButton("close")}
              onMouseLeave={() => setHoveredButton(null)}
            >
              ×
            </button>
          </div>

          <div
            style={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={styles.modalBody}>
              {/* Shift Summary */}
              <div style={styles.shiftSummaryCard}>
                <h2
                  style={{
                    ...styles.sectionTitle,
                    marginBottom: "0.75rem",
                    fontSize: "18px",
                    fontFamily: "inherit",
                  }}
                >
                  {getFullName(editTaskData?.client)}
                </h2>
                <div style={styles.gridTwoCols}>
                  <div>
                    <strong>Date:</strong>{" "}
                    <span>{formatDate(editTaskData?.ShiftData?.Date)}</span>
                  </div>
                  <div>
                    <strong>Time:</strong>{" "}
                    <span>
                      {editTaskData?.ShiftData?.startingTime} -{" "}
                      {editTaskData?.ShiftData?.endingTime}
                    </span>
                  </div>
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <strong>Shift Instructions:</strong>
                  <textarea
                    value={shiftInstructions}
                    onChange={(e) => setShiftInstructions(e.target.value)}
                    style={styles.textarea}
                  />
                </div>
              </div>

              {/* Current Assignment */}
              <div style={styles.currentAssignmentCard}>
                <h4 style={{ ...styles.sectionTitle, marginBottom: "0.75rem" }}>
                  Current Assignment
                </h4>
                <div style={styles.assignmentDisplay}>
                  {getCurrentAssignmentText()}
                </div>
              </div>

              {/* Assignment Options */}
              <div style={styles.section}>
                <div style={styles.sectionTitle}>Assignment Options</div>

                <div style={styles.flexRow}>
                  <div style={styles.toggleContainer}>
                    <label style={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={assignStaffToggle}
                        onChange={toggleAssignStaff}
                        style={styles.toggleInput}
                      />
                      <span
                        style={{
                          ...styles.toggleSlider(assignStaffToggle),
                        }}
                      >
                        <span
                          style={styles.toggleButton(assignStaffToggle)}
                        ></span>
                      </span>
                    </label>
                    <label>Assign Staff</label>
                  </div>

                  <div style={styles.toggleContainer}>
                    <label style={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={openShiftToggle}
                        onChange={toggleOpenShift}
                        style={styles.toggleInput}
                      />
                      <span
                        style={{
                          ...styles.toggleSlider(openShiftToggle),
                        }}
                      >
                        <span
                          style={styles.toggleButton(openShiftToggle)}
                        ></span>
                      </span>
                    </label>
                    <label>Open Shift</label>
                  </div>
                </div>
              </div>

              {/* Staff Assignment Section */}
              {assignStaffToggle && (
                <div style={styles.section}>
                  <div style={styles.sectionTitle}>Select Staff Members</div>

                  <div style={{ ...styles.flexRow, gap: "1rem" }}>
                    <div style={styles.flexColumn}>
                      <label style={styles.label}>Job Title</label>
                      <select
                        value={jobTitleFilter}
                        onChange={(e) => setJobTitleFilter(e.target.value)}
                        style={styles.select}
                      >
                        <option value="all">All Job Title</option>
                        {jobTitleOptions.map((item) => {
                          return (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div style={styles.flexColumn}>
                      <label style={styles.label}>Criteria</label>
                      <select
                        value={staffCriteria}
                        onChange={(e) => setStaffCriteria(e.target.value)}
                        style={styles.select}
                      >
                        <option value="weeklyHoursAssigned">
                          Assigned Shift Hours
                        </option>
                        <option value="distance">Distance</option>
                        <option value="performance">Performance</option>
                      </select>
                    </div>
                  </div>

                  <div style={styles.staffGrid}>
                    {isLoadingStaff ? (
                      <div
                        style={{
                          gridColumn: "1 / -1",
                          textAlign: "center",
                          padding: "2rem",
                        }}
                      >
                        Loading available staff...
                      </div>
                    ) : availableStaffData?.length === 0 ? (
                      <div
                        style={{ gridColumn: "1 / -1", ...styles.emptyState }}
                      >
                        {availableStaffData.some(
                          (staff) => staff.isAlreadyAssigned
                        )
                          ? "No additional staff available for this shift. Only showing already assigned staff."
                          : "No staff available for this shift"}
                      </div>
                    ) : (
                      availableStaffData.map((staff) => {
                        const isSelected = staff.selected;
                        return (
                          <div
                            key={staff.id}
                            style={styles.staffCard(isSelected)}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStaffSelection(staff.id)}
                              style={styles.checkbox}
                            />
                            <div style={{ flex: "1" }}>
                              <div style={styles.staffName}>
                                {staff.name}
                                {staff.isAlreadyAssigned && staff.selected && (
                                  <span
                                    style={{
                                      marginLeft: "0.5rem",
                                      fontSize: "0.75rem",
                                      color: "#9e61e7",
                                      fontWeight: "normal",
                                    }}
                                  >
                                    (Already Assigned)
                                  </span>
                                )}
                              </div>
                              <div style={styles.staffInfo}>
                                {staff.role || staff.jobTitle}
                                {getCriteriaInfo(staff)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Open Shift Section */}
              {openShiftToggle && (
                <div>
                  <div style={styles.sectionTitle}>Open Shift Details</div>
                  <div>
                    <h4
                      style={{
                        ...styles.label,
                        marginBottom: "1rem",
                        fontSize: "0.875rem",
                      }}
                    >
                      Assigned Staff Members
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.75rem",
                      }}
                    >
                      {selectedAdminAcceptedStaff.length === 0 ? (
                        <div style={styles.emptyState}>
                          No staff have been assigned to this open shift yet
                        </div>
                      ) : (
                        selectedAdminAcceptedStaff.map((staff) => {
                          return (
                            <div
                              key={staff.id}
                              style={styles.acceptedStaffCard}
                            >
                              <div style={styles.avatar}>
                                {staff.image ? (
                                  <img
                                    src={staff.image}
                                    alt={staff.name}
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  staff.avatar
                                )}
                              </div>
                              <div style={{ flex: "1" }}>
                                <div
                                  style={{
                                    ...styles.staffName,
                                    marginBottom: "0.25rem",
                                  }}
                                >
                                  {staff.name}
                                </div>
                                <div
                                  style={{
                                    ...styles.staffInfo,
                                    display: "flex",
                                    gap: "1rem",
                                  }}
                                >
                                  <span>{staff.title}</span>
                                  <span>Accepted {staff.acceptedTime}</span>
                                  {/*
                                  <span>{staff.distance} away</span>
                                  <span>Performance: {staff.performance}%</span>
                                  <span>Hours: {staff.shiftHrs}</span>*/}
                                </div>
                              </div>
                              <div style={styles.acceptedBadge}>Accepted</div>
                              <button
                                onClick={() => unassignStaff(staff.id)}
                                style={{
                                  ...styles.dangerButton,
                                  ...(hoveredButton === `unassign-${staff.id}`
                                    ? styles.dangerButtonHover
                                    : {}),
                                }}
                                onMouseEnter={() =>
                                  setHoveredButton(`unassign-${staff.id}`)
                                }
                                onMouseLeave={() => setHoveredButton(null)}
                              >
                                Unassign
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={closePopup}
                style={{
                  ...styles.secondaryButton,
                  ...(hoveredButton === "cancel"
                    ? styles.secondaryButtonHover
                    : {}),
                }}
                onMouseEnter={() => setHoveredButton("cancel")}
                onMouseLeave={() => setHoveredButton(null)}
              >
                Cancel
              </button>
              <button
                onClick={saveAssignment}
                style={{
                  ...styles.primaryButton,
                  padding: "0.5rem 1.25rem",
                  ...(hoveredButton === "save"
                    ? styles.primaryButtonHover
                    : {}),
                }}
                onMouseEnter={() => setHoveredButton("save")}
                onMouseLeave={() => setHoveredButton(null)}
              >
                Save Assignment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyShiftUpdate;
